import React from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { AuthProvider } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { MicrosoftAuthProvider } from './context/MicrosoftAuthProvider';
import { sendFrontendLogs } from './api';
import translations from './translations';
import logger from './utils/logger';
import seqLogger from './utils/seqLogger';

// Debug directly access translation data (only in development)
logger.debug("DIRECT TRANSLATION CHECK: All languages:", Object.keys(translations));
logger.debug("DIRECT TRANSLATION CHECK: Dutch translations loaded:", !!translations.nl);
logger.debug("DIRECT TRANSLATION CHECK: Dutch 'disclaimer' translation:", translations.nl?.disclaimer);
logger.debug("DIRECT TRANSLATION CHECK: Dutch 'pleaseReadAndAccept' translation:", translations.nl?.pleaseReadAndAccept);
logger.debug("DIRECT TRANSLATION CHECK: Dutch 'accept' translation:", translations.nl?.accept);

// Initialize SEQ logging
// Store original console before it gets overridden
if (typeof window !== 'undefined') {
  window.__ORIGINAL_CONSOLE__ = {
    log: console.log.bind(console),
    error: console.error.bind(console),
    warn: console.warn.bind(console),
    info: console.info.bind(console)
  };
}

const seqInitResult = seqLogger.init();

// Set up console log capture for the frontend logger
(function setupConsoleLogger() {
  // Store original console methods
  const originalConsole = {
    log: console.log,
    info: console.info,
    warn: console.warn,
    error: console.error
  };

  // Array to store captured logs
  const capturedLogs = [];

  // Maximum number of logs to store before sending
  const MAX_LOGS = 50;

  // Flag to indicate if logs are being sent
  let isSending = false;

  // Format datetime to match backend format: YYYY-MM-DD HH:MM:SS,sss
  const formatTimestamp = (date) => {
    const pad = (num, size = 2) => String(num).padStart(size, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    const milliseconds = pad(date.getMilliseconds(), 3);

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds},${milliseconds}`;
  };

  // Function to send logs to backend
  const sendLogs = async () => {
    if (isSending || capturedLogs.length === 0) return;
    
    isSending = true;
    
    try {
      // Clone logs to send
      const logsToSend = [...capturedLogs];
      
      // Clear captured logs array
      capturedLogs.length = 0;
      
      // Send logs to backend
      await sendFrontendLogs(logsToSend);
    } catch (error) {
      logger.error('Error sending logs to backend:', error);
    } finally {
      isSending = false;
    }
  };

  // Override console methods to capture logs
  console.log = function() {
    const args = Array.from(arguments);
    originalConsole.log.apply(console, args);
    const logMessage = args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : String(arg)).join(' ');
    
    capturedLogs.push({
      timestamp: formatTimestamp(new Date()),
      level: 'INFO',
      module: 'console',
      message: logMessage
    });
    
    // Send to SEQ if enabled
    if (seqLogger.isEnabled()) {
      seqLogger.info(logMessage, 'Console');
    }
    
    if (capturedLogs.length >= MAX_LOGS) {
      sendLogs();
    }
  };

  console.info = function() {
    const args = Array.from(arguments);
    originalConsole.info.apply(console, args);
    const logMessage = args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : String(arg)).join(' ');
    
    capturedLogs.push({
      timestamp: formatTimestamp(new Date()),
      level: 'INFO',
      module: 'console',
      message: logMessage
    });
    
    // Send to SEQ if enabled
    if (seqLogger.isEnabled()) {
      seqLogger.info(logMessage, 'Console');
    }
    
    if (capturedLogs.length >= MAX_LOGS) {
      sendLogs();
    }
  };

  console.warn = function() {
    const args = Array.from(arguments);
    originalConsole.warn.apply(console, args);
    const logMessage = args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : String(arg)).join(' ');
    
    capturedLogs.push({
      timestamp: formatTimestamp(new Date()),
      level: 'WARNING',
      module: 'console',
      message: logMessage
    });
    
    // Send to SEQ if enabled
    if (seqLogger.isEnabled()) {
      seqLogger.warn(logMessage, 'Console');
    }
    
    if (capturedLogs.length >= MAX_LOGS) {
      sendLogs();
    }
  };

  console.error = function() {
    const args = Array.from(arguments);
    originalConsole.error.apply(console, args);
    const logMessage = args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : String(arg)).join(' ');
    
    capturedLogs.push({
      timestamp: formatTimestamp(new Date()),
      level: 'ERROR',
      module: 'console',
      message: logMessage
    });
    
    // Send to SEQ if enabled (immediately for errors)
    if (seqLogger.isEnabled()) {
      seqLogger.error(logMessage, 'Console');
    }
    
    // Send immediately for errors
    sendLogs();
  };

  // Set interval to periodically send logs (every 30 seconds)
  setInterval(sendLogs, 30000);

  // Send logs when page is about to unload
  window.addEventListener('beforeunload', (event) => {
    if (capturedLogs.length > 0) {
      // Skip the logging for Microsoft authentication redirects to avoid errors
      const currentUrl = window.location.href;
      const isMicrosoftAuthRedirect = 
        currentUrl.includes('/auth/microsoft') || 
        currentUrl.includes('login.microsoftonline.com') ||
        currentUrl.includes('/login/microsoft') ||
        currentUrl.includes('/login?error=No%20user%20account');
      
      if (isMicrosoftAuthRedirect) {
        // Skip logging during Microsoft redirects
        return;
      }
      
      // Use Navigator.sendBeacon which is designed for this scenario
      // and doesn't block page unload or cause errors
      try {
        const blob = new Blob([JSON.stringify({ logs: capturedLogs })], {
          type: 'application/json'
        });
        
        navigator.sendBeacon('/api/logs/frontend', blob);
      } catch (e) {
        // Silent fail - we don't want to disrupt page navigation
        // if logging fails
      }
    }
  });
})();

// Wrap everything in proper providers to ensure translations work
const container = document.getElementById('root');
const root = createRoot(container);
root.render(
  <React.StrictMode>
    <LanguageProvider>
      <MicrosoftAuthProvider>
        <AuthProvider>
          <App />
        </AuthProvider>
      </MicrosoftAuthProvider>
    </LanguageProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals(); 